import React from 'react';
import { Typography, Row, Image, Card, Col } from 'antd';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from './PageLayout';
import piuma_castellaccio from '../components/img/piuma_castellaccio.jpg';
import { CheckOutInstructions } from '../components/welcome/CheckOutCard';
import { CoffeeMachineInstructions } from '../components/welcome/CoffeeCard';
import { RubbishInstructions } from '../components/welcome/RubbishCard';
import { UsefulContacts } from '../components/welcome/ContactsCard';

const { Title } = Typography;

// Individual Card Components
const CheckOutCard = ({ navigate }) => {
  const { t } = useTranslation();
  
  return (
    <Col xs={24} sm={12} md={6}>
      <Card 
        hoverable
        style={{ height: '100%', textAlign: 'center' }}
        onClick={() => navigate('/welcome/checkout')}
        cover={<div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>🏁</div>}
      >
        <Card.Meta 
          title={<div style={{ textAlign: 'center' }}>{t('cardTitles.checkout')}</div>} 
          description={<div style={{ textAlign: 'center' }}>{t('cardDescriptions.checkout')}</div>} 
        />
      </Card>
    </Col>
  );
};

const CoffeeCard = ({ navigate }) => {
  const { t } = useTranslation();
  
  return (
    <Col xs={24} sm={12} md={6}>
      <Card 
        hoverable
        style={{ height: '100%', textAlign: 'center' }}
        onClick={() => navigate('/welcome/coffee')}
        cover={<div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>☕</div>}
      >
        <Card.Meta 
          title={<div style={{ textAlign: 'center' }}>{t('cardTitles.coffee')}</div>} 
          description={<div style={{ textAlign: 'center' }}>{t('cardDescriptions.coffee')}</div>} 
        />
      </Card>
    </Col>
  );
};

const RubbishCard = ({ navigate }) => {
  const { t } = useTranslation();
  
  return (
    <Col xs={24} sm={12} md={6}>
      <Card 
        hoverable
        style={{ height: '100%', textAlign: 'center' }}
        onClick={() => navigate('/welcome/rubbish')}
        cover={<div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>🗑️</div>}
      >
        <Card.Meta 
          title={<div style={{ textAlign: 'center' }}>{t('cardTitles.rubbish')}</div>} 
          description={<div style={{ textAlign: 'center' }}>{t('cardDescriptions.rubbish')}</div>} 
        />
      </Card>
    </Col>
  );
};

const ContactsCard = ({ navigate }) => {
  const { t } = useTranslation();
  
  return (
    <Col xs={24} sm={12} md={6}>
      <Card 
        hoverable
        style={{ height: '100%', textAlign: 'center' }}
        onClick={() => navigate('/welcome/contacts')}
        cover={<div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>📞</div>}
      >
        <Card.Meta 
          title={<div style={{ textAlign: 'center' }}>{t('cardTitles.contacts')}</div>} 
          description={<div style={{ textAlign: 'center' }}>{t('cardDescriptions.contacts')}</div>} 
        />
      </Card>
    </Col>
  );
};

// Main navigation cards container
const InstructionCards = ({ navigate }) => {
  return (
    <Row gutter={[16, 16]}>
      <CheckOutCard navigate={navigate} />
      <CoffeeCard navigate={navigate} />
      <RubbishCard navigate={navigate} />
      <ContactsCard navigate={navigate} />
    </Row>
  );
};

// Hero image component for the welcome page
const WelcomeHero = ({ guestName }) => {
  const { t } = useTranslation();
  
  return (
    <Card
      bordered={false}
      style={{
        borderRadius: "12px",
        overflow: "hidden",
        marginBottom: "48px",
        position: "relative",
        height: "400px",
      }}
      bodyStyle={{
        padding: 0,
        height: "400px",
        position: "relative",
      }}
    >
      <Image
        src={piuma_castellaccio}
        alt="Ca' De Campo"
        preview={false}
        width="100%"
        height="400px"
        style={{
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          padding: "0 24px",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Title level={2} style={{ color: "#fff", margin: "24px 0 0 0", textAlign: "center" }}>
          {t('welcome.greeting', { name: guestName })}
        </Title>
        <Title level={4} style={{ color: "#fff", margin: "16px 0 0 0", fontWeight: "normal", textAlign: "center" }}>
          {t('welcome.thankyou')}
        </Title>

        <Title level={4} style={{ color: "#fff", margin: "0 0 0 0", fontWeight: "normal", textAlign: "center" }}>
          {t('welcome.instructions')}
        </Title>
      </div>
    </Card>
  );
};

// Individual instruction page component with back button
const InstructionPage = ({ children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const goBack = () => {
    navigate('/welcome');
  };
  
  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <a onClick={goBack}>{t('navigation.back')}</a>
      </div>
      {children}
    </>
  );
};

// Welcome homepage component
const WelcomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guestName = queryParams.get('guestName');
  
  return (
    <>
      <WelcomeHero guestName={guestName} />
      <InstructionCards navigate={navigate} />
    </>
  );
};

const QRCodeWelcome = () => {
  return (
    <PageLayout>
      <div style={{ padding: '0 0 30px 0', position: 'relative' }}>
        <Routes>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/checkout" element={
            <InstructionPage>
              <CheckOutInstructions />
            </InstructionPage>
          } />
          <Route path="/coffee" element={
            <InstructionPage>
              <CoffeeMachineInstructions />
            </InstructionPage>
          } />
          <Route path="/rubbish" element={
            <InstructionPage>
              <RubbishInstructions />
            </InstructionPage>
          } />
          <Route path="/contacts" element={
            <InstructionPage>
              <UsefulContacts />
            </InstructionPage>
          } />
        </Routes>
      </div>
    </PageLayout>
  );
};

export default QRCodeWelcome;